import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import './InfoTip.css'

/**
 * The (i) beside a number, and what it actually counts.
 *
 * The design uses this on the funnel boxes — "Tap the (i) on a box for what it
 * counts" — and it is the right shape for any figure carrying a caveat: the
 * caveat is there when you want it and out of the way when you do not.
 *
 * A button rather than a hover title. A `title` attribute never appears on a
 * touch screen, and half the places this is needed are read on a phone.
 *
 * Click to open, click anywhere or press Escape to close. Not hover-to-open:
 * a tooltip that appears while the pointer is passing through is noise, and one
 * that vanishes when you move toward it cannot be read at all.
 *
 * ## It is positioned in the viewport, not beside the button
 *
 * The 15 Aug browser test found this twice. Anchored to the icon with
 * `position: absolute`, the popover on a right-hand KPI ran off the edge of a
 * phone screen with every line cut mid-word, and the one on the ambassador row
 * opened upward into the page heading and lost its first two sentences. Both
 * were unreadable, and neither could be scrolled to.
 *
 * So it is fixed-positioned and measured after it renders: clamped into the
 * window horizontally, and flipped below the button when there is not room
 * above. A popover that cannot be read is worse than no popover, because the
 * caveat it carries is the reason the number is trustworthy.
 */
export function InfoTip({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const wrap = useRef<HTMLSpanElement>(null)
  const body = useRef<HTMLSpanElement>(null)
  const id = useId()

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  /**
   * Place it once it has a size.
   *
   * Layout effect rather than effect: this runs before the browser paints, so
   * the popover never appears in the wrong place first and then jumps.
   */
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const anchor = wrap.current?.getBoundingClientRect()
      const box = body.current?.getBoundingClientRect()
      if (!anchor || !box) return
      const margin = 8
      // Centred on the icon, then pushed back inside whichever edge it crossed.
      const wanted = anchor.left + anchor.width / 2 - box.width / 2
      const left = Math.min(
        Math.max(margin, wanted),
        Math.max(margin, window.innerWidth - box.width - margin),
      )
      // Above by preference — it must not cover the figure it explains — and
      // below when there is no room, which is what a heading at the top of the
      // page leaves.
      const above = anchor.top - box.height - margin
      const top = above >= margin ? above : anchor.bottom + margin
      setPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  return (
    <span className="infotip" ref={wrap}>
      <button
        className="infotip__btn"
        aria-label={`What "${label}" counts`}
        aria-expanded={open}
        aria-controls={id}
        onClick={(e) => {
          // The lead pane closes its drawer on any click that reaches it, and a
          // row may select on click; neither should happen because somebody
          // asked what a number means.
          e.stopPropagation()
          setOpen((v) => !v)
        }}
      >
        i
      </button>
      {open && (
        <span
          className="infotip__body"
          id={id}
          role="note"
          ref={body}
          // Hidden until measured, so it is never painted off-screen first.
          style={pos ? { top: pos.top, left: pos.left } : { opacity: 0 }}
        >
          {children}
        </span>
      )}
    </span>
  )
}
